import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resellerService } from '../services/reseller.service';
import { ActivationState, ActivationStep } from '../types/reseller.types';
import { extractErrorMessage, extractErrorFromResponse } from '../helpers/errorHandler';

const STORAGE_KEY = 'truhu_activation_code';
const STORAGE_RESELLER_KEY = 'truhu_activation_reseller';

interface StoredResellerInfo {
  name?: string;
  code?: string;
  logoUrl?: string;
}

interface UseActivationReturn {
  state: ActivationState;
  validateCode: (code: string) => Promise<void>;
  activateCode: () => Promise<void>;
  setStep: (step: ActivationStep) => void;
  resetActivation: () => Promise<void>;
  setError: (error: string) => void;
  clearStoredCode: () => void;
  getStoredCode: () => string | null;
}

export const useActivation = (): UseActivationReturn => {
  const [searchParams] = useSearchParams();
  const resellerNameFromUrl = searchParams.get('r');
  
  const isInitialMount = useRef(true);
  const prevResellerName = useRef<string | null>(null);

  const getInitialState = (): ActivationState => {
    const storedCode = sessionStorage.getItem(STORAGE_KEY);
    const storedResellerStr = sessionStorage.getItem(STORAGE_RESELLER_KEY);
    
    console.log('[useActivation] Initializing state...', {
      hasStoredCode: !!storedCode,
      hasStoredReseller: !!storedResellerStr,
      resellerNameFromUrl
    });
    
    if (storedCode && storedResellerStr) {
      try {
        const storedReseller: StoredResellerInfo = JSON.parse(storedResellerStr);
        console.log('[useActivation] Restoring from sessionStorage:', {
          code: storedCode,
          reseller: storedReseller
        });
        return {
          step: 'code-valid',
          couponCode: storedCode,
          fullCode: storedCode,
          resellerName: storedReseller.name,
          resellerCode: storedReseller.code,
          resellerLogoUrl: storedReseller.logoUrl,
          isLoading: false
        };
      } catch (e) {
        console.error('[useActivation] Failed to parse stored data:', e);
      }
    }
    
    console.log('[useActivation] Starting fresh, step:', resellerNameFromUrl ? 'loading' : 'enter-code');
    return {
      step: resellerNameFromUrl ? 'loading' : 'enter-code',
      couponCode: '',
      fullCode: '',
      isLoading: resellerNameFromUrl ? true : false
    };
  };

  const [state, setState] = useState<ActivationState>(getInitialState());

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevResellerName.current = resellerNameFromUrl;
      return;
    }
    
    if (prevResellerName.current !== resellerNameFromUrl) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_RESELLER_KEY);
      setState({
        step: resellerNameFromUrl ? 'loading' : 'enter-code',
        couponCode: '',
        fullCode: '',
        isLoading: resellerNameFromUrl ? true : false
      });
    }
    
    prevResellerName.current = resellerNameFromUrl;
  }, [resellerNameFromUrl]);

  useEffect(() => {
    const storedCode = sessionStorage.getItem(STORAGE_KEY);
    const storedResellerStr = sessionStorage.getItem(STORAGE_RESELLER_KEY);
    
    if (storedCode && storedResellerStr) {
      try {
        const storedReseller: StoredResellerInfo = JSON.parse(storedResellerStr);
        setState({
          step: 'code-valid',
          couponCode: storedCode,
          fullCode: storedCode,
          resellerName: storedReseller.name,
          resellerCode: storedReseller.code,
          resellerLogoUrl: storedReseller.logoUrl,
          isLoading: false
        });
        return;
      } catch (e) {
        console.error('Failed to parse stored reseller info:', e);
      }
    }

    const loadResellerFromUrl = async () => {
      if (!resellerNameFromUrl) return;
      
      setState((prev) => ({
        ...prev,
        step: 'loading',
        isLoading: true
      }));

      try {
        const response = await resellerService.getResellerByName(resellerNameFromUrl);
        if (response.isSuccess && response.responseObject) {
          const reseller = response.responseObject;
          setState((prev) => ({
            ...prev,
            step: 'enter-code',
            resellerCode: reseller.code,
            resellerName: reseller.name,
            resellerLogoUrl: reseller.logoUrl,
            isLoading: false
          }));
        } else {
          setState((prev) => ({
            ...prev,
            step: 'error',
            error: 'Reseller not found',
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Failed to load reseller info:', error);
        setState((prev) => ({
          ...prev,
          step: 'error',
          error: 'Failed to load reseller information',
          isLoading: false
        }));
      }
    };

    loadResellerFromUrl();
  }, [resellerNameFromUrl]);

  const validateCode = useCallback(async (couponCode: string) => {
    const fullCode = couponCode.toUpperCase();

    setState((prev) => ({
      ...prev,
      couponCode,
      fullCode,
      step: 'validating',
      isLoading: true,
      error: undefined,
      validationMessage: undefined
    }));

    try {
      const response = await resellerService.validateCode(fullCode, resellerNameFromUrl || undefined);

      if (response.isSuccess && response.responseObject?.isValid) {
        console.log('[useActivation] Code valid, storing to sessionStorage:', fullCode);
        sessionStorage.setItem(STORAGE_KEY, fullCode);
        
        const resellerInfo: StoredResellerInfo = {
          name: response.responseObject?.resellerName || state.resellerName,
          code: response.responseObject?.resellerCode || state.resellerCode,
          logoUrl: response.responseObject?.resellerLogoUrl || state.resellerLogoUrl
        };
        sessionStorage.setItem(STORAGE_RESELLER_KEY, JSON.stringify(resellerInfo));
        console.log('[useActivation] Stored reseller info:', resellerInfo);

        setState((prev) => ({
          ...prev,
          step: 'code-valid',
          fullCode,
          resellerName: resellerInfo.name,
          resellerCode: resellerInfo.code,
          resellerLogoUrl: resellerInfo.logoUrl,
          validationMessage: response.responseObject?.message,
          isLoading: false
        }));
      } else {
        const validationMessage = extractErrorFromResponse(response, 'Invalid code');
        
        setState((prev) => ({
          ...prev,
          step: 'code-invalid',
          validationMessage,
          isLoading: false
        }));
      }
    } catch (error: any) {
      const validationMessage = extractErrorMessage(error, 'Failed to validate code. Please try again.');
      
      setState((prev) => ({
        ...prev,
        step: 'code-invalid',
        validationMessage,
        isLoading: false
      }));
    }
  }, [state.resellerCode, state.resellerName, state.resellerLogoUrl, resellerNameFromUrl]);

  const activateCode = useCallback(async () => {
    const storedCode = sessionStorage.getItem(STORAGE_KEY) || state.fullCode;

    if (!storedCode) {
      setState((prev) => ({
        ...prev,
        step: 'error',
        error: 'No activation code found. Please start over.'
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      step: 'redeeming',
      isLoading: true,
      error: undefined
    }));

    try {
      const response = await resellerService.redeemCode(storedCode);

      if (response.isSuccess && response.responseObject?.success) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_RESELLER_KEY);

        setState((prev) => ({
          ...prev,
          step: 'success',
          subscriptionEndDate: response.responseObject?.subscriptionEndDate,
          resellerName: response.responseObject?.resellerName || prev.resellerName,
          resellerLogoUrl: response.responseObject?.resellerLogoUrl || prev.resellerLogoUrl,
          userEmail: response.responseObject?.userEmail,
          userFirstName: response.responseObject?.userFirstName,
          userLastName: response.responseObject?.userLastName,
          isLoading: false
        }));
      } else {
        const errorMessage = extractErrorFromResponse(response, 'Failed to activate subscription');
        
        setState((prev) => ({
          ...prev,
          step: 'error',
          error: errorMessage,
          isLoading: false
        }));
      }
    } catch (error: any) {
    
      const errorMessage = extractErrorMessage(error, 'Failed to activate subscription. Please try again.');
      setState((prev) => ({
        ...prev,
        step: 'error',
        error: errorMessage,
        isLoading: false
      }));
    }
  }, [state.fullCode]);

  const setStep = useCallback((step: ActivationStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  const setError = useCallback((error: string) => {
    setState((prev) => ({ ...prev, step: 'error', error }));
  }, []);

  const resetActivation = useCallback(async () => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_RESELLER_KEY);
    
    if (resellerNameFromUrl && (!state.resellerName || !state.resellerCode)) {
      setState((prev) => ({
        ...prev,
        step: 'loading',
        couponCode: '',
        fullCode: '',
        isLoading: true,
        error: undefined,
        validationMessage: undefined,
        subscriptionEndDate: undefined
      }));
      
      try {
        const response = await resellerService.getResellerByName(resellerNameFromUrl);
        if (response.isSuccess && response.responseObject) {
          const reseller = response.responseObject;
          setState((prev) => ({
            ...prev,
            step: 'enter-code',
            resellerCode: reseller.code,
            resellerName: reseller.name,
            resellerLogoUrl: reseller.logoUrl,
            isLoading: false
          }));
        } else {
          setState((prev) => ({
            ...prev,
            step: 'error',
            error: 'Reseller not found',
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Failed to reload reseller info:', error);
        setState((prev) => ({
          ...prev,
          step: 'error',
          error: 'Failed to load reseller information',
          isLoading: false
        }));
      }
    } else {
      setState((prev) => ({
        step: 'enter-code',
        couponCode: '',
        fullCode: '',
        isLoading: false,
        error: undefined,
        validationMessage: undefined,
        resellerName: prev.resellerName,
        resellerCode: prev.resellerCode,
        resellerLogoUrl: prev.resellerLogoUrl,
        subscriptionEndDate: undefined
      }));
    }
  }, [resellerNameFromUrl, state.resellerName, state.resellerCode]);

  const clearStoredCode = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_RESELLER_KEY);
  }, []);

  const getStoredCode = useCallback(() => {
    return sessionStorage.getItem(STORAGE_KEY);
  }, []);

  return {
    state,
    validateCode,
    activateCode,
    setStep,
    resetActivation,
    setError,
    clearStoredCode,
    getStoredCode
  };
};
